import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Upload, Trash2, Download, Loader2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";

export default function DocumentUpload({ relatedToType, relatedToId, relatedToName }) {
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ["documents", relatedToType, relatedToId],
    queryFn: () => base44.entities.Document.filter({ 
      related_to_type: relatedToType, 
      related_to_id: relatedToId 
    }, "-created_date"),
    enabled: !!relatedToId,
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (id) => base44.entities.Document.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documents", relatedToType, relatedToId] });
    },
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      
      await base44.entities.Document.create({
        file_name: file.name,
        file_url: file_url,
        file_size: file.size,
        uploaded_by: user?.email,
        uploaded_by_name: user?.full_name,
        related_to_type: relatedToType,
        related_to_id: relatedToId,
        related_to_name: relatedToName,
      });

      queryClient.invalidateQueries({ queryKey: ["documents", relatedToType, relatedToId] });
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-zinc-900" />
          <h3 className="font-semibold text-slate-900">Dokumenter ({documents.length})</h3>
        </div>
        <label className="cursor-pointer">
          <input
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            disabled={uploading}
          />
          <Button variant="outline" size="sm" disabled={uploading} asChild>
            <span>
              {uploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Last opp
            </span>
          </Button>
        </label>
      </div>

      {/* Documents List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map(i => (
            <div key={i} className="p-3 bg-slate-50 rounded-lg animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-2/3 mb-2" />
              <div className="h-3 bg-slate-200 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : documents.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-4">Ingen dokumenter ennå</p>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="p-2 bg-white rounded">
                  <FileText className="w-4 h-4 text-zinc-900" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm truncate">{doc.file_name}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>•</span>
                    <span>{doc.uploaded_by_name}</span>
                    <span>•</span>
                    <span>{formatDistanceToNow(new Date(doc.created_date), { addSuffix: true, locale: nb })}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Download className="w-4 h-4 text-slate-400 hover:text-zinc-900" />
                  </Button>
                </a>
                {doc.uploaded_by === user?.email && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => deleteDocumentMutation.mutate(doc.id)}
                  >
                    <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}